export enum LabelFormatName {
  brotherLabel = "brotherLabel",
  dymoLabelWriter = "dymoLabelWriter",
}

export enum LabelLayout {
  sideBySide = "sideBySide",
  stacked = "stacked",
}

export interface LabelFormat {
  dotsPerInch: number;
  heightMm: number;
  layout: LabelLayout;
  logoAsset: string;
  logoHeightRatio: number;
  paddingRatio: number;
  paperSizeName: string;
  printerName: string;
  qrFillRatio: number;
  textSizeRatio: number;
  widthMm: number;
}

export interface LabelGeometry {
  boxHeightMm: number;
  boxWidthMm: number;
  fontSizeMm: number;
  footerSizeMm: number;
  gapMm: number;
  logoHeightMm: number;
  paddingMm: number;
  qrPixels: number;
  qrSizeMm: number;
  textWidthMm: number;
}

const millimetresPerInch = 25.4;
const maxQrPixels = 2000;
const bleedMm = 0.3;

export const labelFormats: Record<LabelFormatName, LabelFormat> = {
  [LabelFormatName.brotherLabel]: {
    dotsPerInch: 300,
    heightMm: 164,
    layout: LabelLayout.stacked,
    logoAsset: "assets/logo/dark_logo_vertical.png",
    logoHeightRatio: 0.13,
    paddingRatio: 0.05,
    paperSizeName: "103 x 164 mm",
    printerName: "Brother",
    qrFillRatio: 0.5,
    textSizeRatio: 0.025,
    widthMm: 103,
  },
  [LabelFormatName.dymoLabelWriter]: {
    dotsPerInch: 300,
    heightMm: 25.4,
    layout: LabelLayout.sideBySide,
    logoAsset: "assets/logo/dark_logo_header.png",
    logoHeightRatio: 0.22,
    paddingRatio: 0.04,
    paperSizeName: '30336 — 1" x 2-1/8"',
    printerName: "DYMO LabelWriter",
    qrFillRatio: 1,
    textSizeRatio: 0.085,
    widthMm: 53.98,
  },
};

export function printDialogHint(formatName: LabelFormatName): string {
  const format = labelFormats[formatName];

  return `${format.printerName} — set Paper size to ${format.paperSizeName}, Margins to None, Scale to 100%.`;
}

export function resolveLabelGeometry(format: LabelFormat): LabelGeometry {
  const boxWidthMm = format.widthMm - bleedMm;
  const boxHeightMm = format.heightMm - bleedMm;
  const paddingMm = Math.min(boxWidthMm, boxHeightMm) * format.paddingRatio;
  const contentWidthMm = boxWidthMm - paddingMm * 2;
  const contentHeightMm = boxHeightMm - paddingMm * 2;
  const logoHeightMm = boxHeightMm * format.logoHeightRatio;
  const fontSizeMm = boxHeightMm * format.textSizeRatio;

  const availableQrMm =
    format.layout === LabelLayout.sideBySide
      ? Math.min(contentHeightMm, contentWidthMm / 2)
      : Math.min(contentWidthMm, contentHeightMm - logoHeightMm - paddingMm);
  const qrSizeMm = availableQrMm * format.qrFillRatio;

  const textWidthMm = format.layout === LabelLayout.sideBySide ? contentWidthMm - qrSizeMm - paddingMm : contentWidthMm;

  return {
    boxHeightMm,
    boxWidthMm,
    fontSizeMm,
    footerSizeMm: fontSizeMm * 0.85,
    gapMm: paddingMm,
    logoHeightMm,
    paddingMm,
    qrPixels: Math.min(maxQrPixels, Math.round((qrSizeMm / millimetresPerInch) * format.dotsPerInch)),
    qrSizeMm,
    textWidthMm,
  };
}
